/**
 * Strategy Builder page.
 *
 * A guided, no-DSS-knowledge form for composing and backtesting a trading
 * strategy. The user picks a universe, date window, entry/exit conditions from
 * plain-language templates, risk settings, and optional rigor. The form state
 * is translated to a DSS document (buildDssSpec) and run against the engine via
 * the admin backtest route; results render inline.
 */

import { useMemo, useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  listBacktestSymbols,
  runBacktest,
  ApiError,
  type BacktestReport,
  type BacktestSymbol,
} from '../api/client';
import {
  defaultModel,
  PRESETS,
  CONDITION_KINDS,
  newCondition,
  describeCondition,
  type StrategyModel,
  type Condition,
  type RuleGroup,
  type Interval,
} from '../strategy/model';
import { buildDssSpec } from '../strategy/buildSpec';
import { ResultsView } from '../strategy/ResultsView';

const INTERVAL_OPTIONS: { value: Interval; label: string }[] = [
  { value: 'daily', label: 'Daily' },
  { value: '60min', label: '60 Minute' },
  { value: '30min', label: '30 Minute' },
  { value: '15min', label: '15 Minute' },
  { value: '5min', label: '5 Minute' },
  { value: '1min', label: '1 Minute' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
];

const CRISIS_OPTIONS = ['covid_2020', 'luna_ftx_2022', 'rate_shock_2022', 'svb_2023'];

export function StrategyBuilderPage() {
  const [model, setModel] = useState<StrategyModel>(defaultModel);
  const [report, setReport] = useState<BacktestReport | null>(null);
  const [errorDetail, setErrorDetail] = useState<{ message: string; code?: string; path?: string } | null>(null);
  const [showSpec, setShowSpec] = useState(false);

  const symbolsQuery = useQuery({ queryKey: ['backtestSymbols'], queryFn: listBacktestSymbols });
  const symbols: BacktestSymbol[] = symbolsQuery.data?.data ?? [];

  const { spec, notes } = useMemo(() => buildDssSpec(model), [model]);

  const runMutation = useMutation({
    mutationFn: () => runBacktest(spec),
    onSuccess: (res) => {
      setReport(res.data);
      setErrorDetail(null);
    },
    onError: (err) => {
      setReport(null);
      if (err instanceof ApiError) {
        const body = err.body as { error?: string; code?: string; path?: string } | undefined;
        setErrorDetail({ message: body?.error ?? err.message, code: body?.code, path: body?.path });
      } else {
        setErrorDetail({ message: (err as Error).message });
      }
    },
  });

  const patch = (p: Partial<StrategyModel>) => setModel((m) => ({ ...m, ...p }));

  const canRun = model.symbols.length > 0 && !!model.startDate && !!model.endDate && !runMutation.isPending;

  return (
    <div className="page">
      <div className="page-header-row">
        <h2>Strategy Builder</h2>
      </div>
      <p className="page-subtitle">
        Build a trading strategy with plain-language rules — no spec knowledge needed — then backtest it
        against historical data and see the results. Start from a preset or build from scratch.
      </p>

      {/* Presets */}
      <div className="card">
        <div className="card-title">Start from a preset</div>
        <div className="action-group">
          {PRESETS.map((p) => (
            <button
              key={p.id}
              className="btn btn--secondary btn--small"
              title={p.description}
              onClick={() => { setModel((m) => p.apply(m)); setReport(null); setErrorDetail(null); }}
            >
              {p.label}
            </button>
          ))}
          <button className="btn btn--secondary btn--small" onClick={() => { setModel(defaultModel()); setReport(null); setErrorDetail(null); }}>
            Reset
          </button>
        </div>
      </div>

      {/* Basics */}
      <div className="card">
        <div className="card-title">1. Basics</div>
        <div className="form-row">
          <div className="form-field">
            <label>Strategy name</label>
            <input className="input-field" value={model.name} onChange={(e) => patch({ name: e.target.value })} />
          </div>
          <div className="form-field">
            <label>Direction</label>
            <select className="input-field" value={model.direction} onChange={(e) => patch({ direction: e.target.value as StrategyModel['direction'] })}>
              <option value="long">Long only</option>
              <option value="short">Short only</option>
              <option value="both">Long &amp; Short</option>
            </select>
          </div>
          <div className="form-field">
            <label>Interval</label>
            <select className="input-field" value={model.interval} onChange={(e) => patch({ interval: e.target.value as Interval })}>
              {INTERVAL_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
        </div>
        <div className="form-row">
          <div className="form-field">
            <label>Start date</label>
            <input type="date" className="input-field" value={model.startDate} onChange={(e) => patch({ startDate: e.target.value })} />
          </div>
          <div className="form-field">
            <label>End date</label>
            <input type="date" className="input-field" value={model.endDate} onChange={(e) => patch({ endDate: e.target.value })} />
          </div>
          <div className="form-field">
            <label>Initial cash</label>
            <input type="number" className="input-field" value={model.initialCash} onChange={(e) => patch({ initialCash: Number(e.target.value) })} />
          </div>
        </div>
      </div>

      {/* Universe */}
      <div className="card">
        <div className="card-title">2. Universe</div>
        {symbolsQuery.isLoading && <p className="loading-state">Loading available symbols…</p>}
        {symbolsQuery.isError && <p className="error-text">Failed to load symbols.</p>}
        {!symbolsQuery.isLoading && symbols.length === 0 && (
          <p className="empty-state">No symbols with imported data yet. Import market data first (Data Import).</p>
        )}
        <div className="checkbox-grid" style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {symbols.map((s) => {
            const checked = model.symbols.includes(s.ticker);
            const cov = s.coverage.find((c) => c.interval === model.interval);
            return (
              <label key={s.ticker} className="radio-option" style={{ minWidth: 220 }} title={cov ? `${cov.barCount} bars · ${cov.from.slice(0, 10)}→${cov.to.slice(0, 10)}` : 'No data for this interval'}>
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={(e) => {
                    const next = e.target.checked
                      ? [...model.symbols, s.ticker]
                      : model.symbols.filter((t) => t !== s.ticker);
                    patch({ symbols: next });
                  }}
                />
                <span>
                  <strong>{s.ticker}</strong> <span className={`badge badge--${s.assetType}`}>{s.assetType}</span>
                  {!cov && <span className="badge badge--gray" style={{ marginLeft: 6 }}>no {model.interval} data</span>}
                </span>
              </label>
            );
          })}
        </div>
        <p className="section-description">Selecting multiple symbols runs each independently against a shared cash pool.</p>
      </div>

      {/* Entry / Exit rules */}
      <div className="card">
        <div className="card-title">3. Entry rules</div>
        <RuleGroupEditor
          group={model.entry}
          onChange={(entry) => patch({ entry })}
          emptyHint="Add at least one entry condition."
        />
      </div>

      <div className="card">
        <div className="card-title">4. Exit rules</div>
        <RuleGroupEditor
          group={model.exit}
          onChange={(exit) => patch({ exit })}
          emptyHint="Add an exit condition (or rely on stop-loss / take-profit below)."
        />
      </div>

      {/* Risk */}
      <div className="card">
        <div className="card-title">5. Position sizing &amp; risk</div>
        <div className="form-row">
          <div className="form-field">
            <label>Sizing</label>
            <select className="input-field" value={model.risk.sizingMode} onChange={(e) => patch({ risk: { ...model.risk, sizingMode: e.target.value as StrategyModel['risk']['sizingMode'] } })}>
              <option value="equity_fraction">Fraction of equity</option>
              <option value="fixed_notional">Fixed dollar amount</option>
              <option value="fixed_qty">Fixed share quantity</option>
            </select>
          </div>
          <div className="form-field">
            <label>{model.risk.sizingMode === 'equity_fraction' ? 'Fraction (0–1)' : model.risk.sizingMode === 'fixed_notional' ? 'Dollars' : 'Shares'}</label>
            <input type="number" step="any" className="input-field" value={model.risk.sizingValue} onChange={(e) => patch({ risk: { ...model.risk, sizingValue: Number(e.target.value) } })} />
          </div>
        </div>
        <div className="form-row">
          <div className="form-field">
            <label>Stop-loss % (0 = none)</label>
            <input type="number" step="any" className="input-field" value={model.risk.stopLossPct} onChange={(e) => patch({ risk: { ...model.risk, stopLossPct: Number(e.target.value) } })} />
          </div>
          <div className="form-field">
            <label>Take-profit % (0 = none)</label>
            <input type="number" step="any" className="input-field" value={model.risk.takeProfitPct} onChange={(e) => patch({ risk: { ...model.risk, takeProfitPct: Number(e.target.value) } })} />
          </div>
          <div className="form-field">
            <label>Time stop (bars, 0 = none)</label>
            <input type="number" className="input-field" value={model.risk.timeStopBars} onChange={(e) => patch({ risk: { ...model.risk, timeStopBars: Number(e.target.value) } })} />
          </div>
        </div>
        <div className="form-row">
          <div className="form-field">
            <label>Slippage %</label>
            <input type="number" step="any" className="input-field" value={model.costsSlippagePct} onChange={(e) => patch({ costsSlippagePct: Number(e.target.value) })} />
          </div>
          <div className="form-field">
            <label>Fees %</label>
            <input type="number" step="any" className="input-field" value={model.costsFeesPct} onChange={(e) => patch({ costsFeesPct: Number(e.target.value) })} />
          </div>
        </div>
      </div>

      {/* Advanced rigor */}
      <details className="card">
        <summary className="card-title" style={{ cursor: 'pointer' }}>6. Robustness checks (advanced, optional)</summary>
        <div style={{ marginTop: 12 }}>
          <label className="radio-option">
            <input type="checkbox" checked={model.rigor.walkForward} onChange={(e) => patch({ rigor: { ...model.rigor, walkForward: e.target.checked } })} />
            <span>Walk-forward (in-sample / out-of-sample split — exposes overfitting)</span>
          </label>
          {model.rigor.walkForward && (
            <div className="form-row" style={{ marginLeft: 24 }}>
              <div className="form-field">
                <label>In-sample months</label>
                <input type="number" className="input-field input-field--small" value={model.rigor.walkForwardIsMonths} onChange={(e) => patch({ rigor: { ...model.rigor, walkForwardIsMonths: Number(e.target.value) } })} />
              </div>
              <div className="form-field">
                <label>Out-of-sample months</label>
                <input type="number" className="input-field input-field--small" value={model.rigor.walkForwardOosMonths} onChange={(e) => patch({ rigor: { ...model.rigor, walkForwardOosMonths: Number(e.target.value) } })} />
              </div>
            </div>
          )}
          <label className="radio-option">
            <input type="checkbox" checked={model.rigor.monteCarlo} onChange={(e) => patch({ rigor: { ...model.rigor, monteCarlo: e.target.checked } })} />
            <span>Monte Carlo confidence bands (reshuffles trades)</span>
          </label>
          {model.rigor.monteCarlo && (
            <div className="form-row" style={{ marginLeft: 24 }}>
              <div className="form-field">
                <label>Runs</label>
                <input type="number" className="input-field input-field--small" value={model.rigor.monteCarloRuns} onChange={(e) => patch({ rigor: { ...model.rigor, monteCarloRuns: Number(e.target.value) } })} />
              </div>
            </div>
          )}
          <div style={{ marginTop: 8 }}>
            <div className="detail-label">Crisis stress windows</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {CRISIS_OPTIONS.map((c) => (
                <label key={c} className="radio-option">
                  <input
                    type="checkbox"
                    checked={model.rigor.crisisPresets.includes(c)}
                    onChange={(e) => {
                      const next = e.target.checked
                        ? [...model.rigor.crisisPresets, c]
                        : model.rigor.crisisPresets.filter((x) => x !== c);
                      patch({ rigor: { ...model.rigor, crisisPresets: next } });
                    }}
                  />
                  <span>{c}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
      </details>

      {notes.length > 0 && (
        <div className="section-description">
          {notes.map((n, i) => <div key={i}>ℹ️ {n}</div>)}
        </div>
      )}

      {/* Run */}
      <div className="action-group" style={{ marginTop: 16 }}>
        <button className="btn btn--primary" disabled={!canRun} onClick={() => runMutation.mutate()}>
          {runMutation.isPending ? 'Running backtest…' : 'Run Backtest'}
        </button>
        <button className="btn btn--secondary" onClick={() => setShowSpec((s) => !s)}>
          {showSpec ? 'Hide' : 'Show'} generated spec
        </button>
      </div>
      {!canRun && !runMutation.isPending && (
        <p className="section-description">Select at least one symbol and a start/end date to run.</p>
      )}

      {showSpec && (
        <pre className="cell-mono" style={{ whiteSpace: 'pre-wrap', fontSize: 12, background: '#fafafa', padding: 12, border: '1px solid #e5e7eb', borderRadius: 6 }}>
          {JSON.stringify(spec, null, 2)}
        </pre>
      )}

      {errorDetail && (
        <div className="error-message" style={{ marginTop: 16 }}>
          <strong>Could not run:</strong> {errorDetail.message}
          {errorDetail.code && <span> ({errorDetail.code}{errorDetail.path ? ` at ${errorDetail.path}` : ''})</span>}
        </div>
      )}

      {report && (
        <div style={{ marginTop: 24 }}>
          <ResultsView report={report} />
        </div>
      )}
    </div>
  );
}

// ─── Rule group editor ──────────────────────────────────────────────────────────

function RuleGroupEditor({
  group,
  onChange,
  emptyHint,
}: {
  group: RuleGroup;
  onChange: (g: RuleGroup) => void;
  emptyHint: string;
}) {
  const [addKind, setAddKind] = useState<Condition['kind']>('ma_cross_up');

  const updateCondition = (idx: number, c: Condition) => {
    const conditions = group.conditions.map((old, i) => (i === idx ? c : old));
    onChange({ ...group, conditions });
  };
  const removeCondition = (idx: number) => {
    onChange({ ...group, conditions: group.conditions.filter((_, i) => i !== idx) });
  };

  return (
    <div>
      {group.conditions.length > 1 && (
        <div className="form-field" style={{ maxWidth: 220 }}>
          <label>Combine conditions with</label>
          <select className="input-field" value={group.join} onChange={(e) => onChange({ ...group, join: e.target.value as 'all' | 'any' })}>
            <option value="all">ALL must be true (AND)</option>
            <option value="any">ANY can be true (OR)</option>
          </select>
        </div>
      )}

      {group.conditions.length === 0 && <p className="empty-state">{emptyHint}</p>}

      {group.conditions.map((c, idx) => (
        <div key={idx} className="import-section" style={{ marginBottom: 8 }}>
          <div className="card-header-row">
            <span className="section-description">{describeCondition(c)}</span>
            <button className="btn btn--danger btn--small" onClick={() => removeCondition(idx)}>Remove</button>
          </div>
          <ConditionEditor condition={c} onChange={(nc) => updateCondition(idx, nc)} />
        </div>
      ))}

      <div className="action-group" style={{ marginTop: 8 }}>
        <select className="input-field input-field--small" value={addKind} onChange={(e) => setAddKind(e.target.value as Condition['kind'])}>
          {CONDITION_KINDS.map((k) => <option key={k.kind} value={k.kind}>{k.label}</option>)}
        </select>
        <button className="btn btn--secondary btn--small" onClick={() => onChange({ ...group, conditions: [...group.conditions, newCondition(addKind)] })}>
          + Add condition
        </button>
      </div>
    </div>
  );
}

function NumField({ label, value, onChange, step }: { label: string; value: number; onChange: (v: number) => void; step?: string }) {
  return (
    <div className="form-field">
      <label>{label}</label>
      <input type="number" step={step ?? '1'} className="input-field input-field--small" value={value} onChange={(e) => onChange(Number(e.target.value))} />
    </div>
  );
}

function MaTypeField({ value, onChange }: { value: 'SMA' | 'EMA' | 'WMA'; onChange: (v: 'SMA' | 'EMA' | 'WMA') => void }) {
  return (
    <div className="form-field">
      <label>MA type</label>
      <select className="input-field input-field--small" value={value} onChange={(e) => onChange(e.target.value as 'SMA' | 'EMA' | 'WMA')}>
        <option value="SMA">SMA</option>
        <option value="EMA">EMA</option>
        <option value="WMA">WMA</option>
      </select>
    </div>
  );
}

function ConditionEditor({ condition, onChange }: { condition: Condition; onChange: (c: Condition) => void }) {
  const c = condition;
  switch (c.kind) {
    case 'price_above_ma':
    case 'price_below_ma':
      return (
        <div className="form-row">
          <MaTypeField value={c.maType} onChange={(maType) => onChange({ ...c, maType })} />
          <NumField label="Period" value={c.period} onChange={(period) => onChange({ ...c, period })} />
        </div>
      );
    case 'ma_cross_up':
    case 'ma_cross_down':
      return (
        <div className="form-row">
          <MaTypeField value={c.maType} onChange={(maType) => onChange({ ...c, maType })} />
          <NumField label="Fast period" value={c.fast} onChange={(fast) => onChange({ ...c, fast })} />
          <NumField label="Slow period" value={c.slow} onChange={(slow) => onChange({ ...c, slow })} />
        </div>
      );
    case 'rsi_below':
    case 'rsi_above':
      return (
        <div className="form-row">
          <NumField label="RSI period" value={c.period} onChange={(period) => onChange({ ...c, period })} />
          <NumField label="Level" value={c.level} onChange={(level) => onChange({ ...c, level })} />
        </div>
      );
    case 'breakout_high':
    case 'breakdown_low':
      return (
        <div className="form-row">
          <NumField label="Lookback bars" value={c.period} onChange={(period) => onChange({ ...c, period })} />
        </div>
      );
    case 'price_change_above':
    case 'price_change_below':
      return (
        <div className="form-row">
          <NumField label="Period (bars)" value={c.period} onChange={(period) => onChange({ ...c, period })} />
          <NumField label="Percent" value={c.pct} step="any" onChange={(pct) => onChange({ ...c, pct })} />
        </div>
      );
  }
}
